import { inject, singleton } from 'tsyringe';
import {
  kNameOfNotificationWindowService,
  INotificationWindowService,
} from 'app-core/src/interfaces/notification-window-service.interface';
import { INotificationDisplayDetails } from 'app-core/src/models/notification-display-details.model';
import { LoggerService } from 'app-core/src/services/logger.service';
import {
  ITimerServiceDelegate,
  TimerService,
} from 'app-core/src/services/timer.service';
import { FunctionPropertyNames } from 'app-core/src/types';
import { kNotificationWindowName } from 'app-core/src/constants/constants';
import {
  INotificationWindowManagerService,
  INotificationWindowManagerServiceDelegate,
} from 'app-core/src/interfaces/notification-window-manager-service.interface';
import { RecommendationDatastoreService } from 'app-core/src/services/datastore-service/recommendation-datastore.service';
import { NotificationDetailsBuilderService } from 'app-core/src/services/notification-details-builder.service';
import { ContentRecommendationUserAction } from 'app-core/src/constants/content-recommendation-user-action.enum';
import { Application } from 'app-core/src/models/application.model';
import { Game } from 'app-core/src/models/game.model';
import {
  kNameOfUserActionService,
  IUserActionService,
} from 'app-core/src/interfaces/user-action-service.interface';
import {
  INotificationAnalyticsService,
  kNameOfNotificationAnalyticsService,
} from 'app-core/src/services/notification-analytics/notification-analytics.service';
import { StopWatch } from 'app-core/src/utils/stop-watch';
import { IOWWindowWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-window-wrapper.interface';
import { IOWWindowScreenAnchorWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-window-screen-anchor-wrapper.interface';
import {
  IOWWindowsListenerDelegateProvider,
  IOWWindowsListenerDelegateWrapper,
} from 'app-core/src/interfaces/ow-wrappers/ow-window-listener-delegate-wrapper.interface';
import {
  kDITokenNotificationWindowScreenAnchor,
  kDITokenOwNotificationWindow,
  kDITokenWindowListener,
} from 'app-core/src/constants/injectionTokens';
const kTimeForTesterNotificationInMs = 60000;

// -----------------------------------------------------------------------------
@singleton()
export class NotificationWindowService
  implements
    INotificationWindowService,
    INotificationWindowManagerService,
    ITimerServiceDelegate,
    IOWWindowsListenerDelegateWrapper
{
  private readonly logger = LoggerService.getCategory(
    'NotificationWindowService',
  );

  private readonly listeners: Set<INotificationWindowManagerServiceDelegate>;
  private activeNotificationInfo: INotificationDisplayDetails;
  private isTesterNotification: boolean;

  // ---------------------------------------------------------------------------
  public constructor(
    @inject(kDITokenOwNotificationWindow)
    private readonly owNotificationWindow: IOWWindowWrapper,
    @inject(kDITokenNotificationWindowScreenAnchor)
    private readonly owWindowScreenAnchor: IOWWindowScreenAnchorWrapper,
    @inject(kDITokenWindowListener)
    private readonly owWindowListener: IOWWindowsListenerDelegateProvider,
    private readonly datastoreService: RecommendationDatastoreService,
    private readonly timerService: TimerService,
    private readonly notificationVisibilityStopWatch: StopWatch,
    @inject(kNameOfUserActionService)
    private readonly userActionService: IUserActionService,
    private readonly notifDetailsBuilder: NotificationDetailsBuilderService,
    @inject(kNameOfNotificationAnalyticsService)
    private readonly analyticsService: INotificationAnalyticsService,
  ) {
    this.listeners = new Set<INotificationWindowManagerServiceDelegate>();
    this.timerService.addListener(this);
    this.isTesterNotification = false;
  }

  // ---------------------------------------------------------------------------
  // INotificationWindowManagerService
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async show(
    game: Game,
    application: Application,
    isTester = false,
    useDevEnv: string,
  ) {
    const parentSuffix =
      game.originalGameId !== undefined
        ? ` (sub-game of ${game.originalGameId})`
        : '';

    this.logger.info(
      `Showing recommendation for game: ${game.id} & application:` +
        ` ${application.name}${parentSuffix}`,
    );

    this.registerListeners();

    this.activeNotificationInfo =
      await this.notifDetailsBuilder.getNotificationDetails(
        game,
        application,
        useDevEnv,
      );

    this.owWindowScreenAnchor.start();
    this.startIdleTimer();
    if (isTester) {
      this.isTesterNotification = true;
    }
  }

  // ---------------------------------------------------------------------------
  close(force = false) {
    if (force && this.activeNotificationInfo) {
      const visibilityTimeInSeconds =
        this.notificationVisibilityStopWatch.getTime() / 1000;
      this.notificationVisibilityStopWatch.stop();

      this.analyticsService.reportContentRecommend(
        this.activeNotificationInfo.targetGameId ||
          this.activeNotificationInfo.gameId,

        this.activeNotificationInfo.applicationUid,
        ContentRecommendationUserAction.ForceClosed,
        visibilityTimeInSeconds,
        this.activeNotificationInfo.originalGameId,
      );
    }

    this.owWindowScreenAnchor.stop();
  }

  // ---------------------------------------------------------------------------
  async isNotificationShown(): Promise<boolean> {
    const state = await this.owNotificationWindow.getWindowState();
    if (state.window_state_ex === 'closed') {
      return false;
    }

    return true;
  }

  // ---------------------------------------------------------------------------
  getNotificationInfo(): INotificationDisplayDetails {
    return this.activeNotificationInfo;
  }

  // ---------------------------------------------------------------------------
  private async registerListeners() {
    this.unregisterListeners();
    this.owWindowListener.addListener(this);
  }

  // ---------------------------------------------------------------------------
  private async unregisterListeners() {
    this.owWindowListener.removeListener(this);
  }

  // ---------------------------------------------------------------------------
  private async startIdleTimer() {
    const configs = await this.datastoreService.getGlobalConfigurations();
    const intervalInMs = this.isTesterNotification
      ? kTimeForTesterNotificationInMs
      : configs.autoDismissNotificationsInSeconds * 1000;

    this.timerService.initialize({ intervalInMs });
    this.timerService.start();
  }

  // ---------------------------------------------------------------------------
  private stopIdleTimer() {
    this.timerService.stop();
  }

  // ---------------------------------------------------------------------------
  private triggerListenerEvent(
    eventName: FunctionPropertyNames<INotificationWindowManagerServiceDelegate>,
    ...params: any[]
  ) {
    if (!eventName) {
      return;
    }

    for (const listener of this.listeners) {
      if (!listener[eventName]) {
        continue;
      }

      // eslint-disable-next-line prefer-spread
      listener[eventName].apply(listener, params);
    }
  }

  // ---------------------------------------------------------------------------
  private async handleNotificationViewed() {
    const config = await this.datastoreService.getGlobalConfigurations();
    setTimeout(() => {
      if (!this.isTesterNotification) {
        this.triggerListenerEvent(
          'onNotificationReachedMinTimeToDisplay',
          this.activeNotificationInfo,
        );
      }
    }, config.minimumVisibleTimeToCountInSeconds * 1000);
  }

  // ---------------------------------------------------------------------------
  // ExposableService
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  get token(): string {
    return kNameOfNotificationWindowService;
  }

  // ---------------------------------------------------------------------------
  // NotificationWindowManagerServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  addListener(listener: INotificationWindowManagerServiceDelegate) {
    this.listeners.add(listener);
  }

  // ---------------------------------------------------------------------------
  removeListener(listener: INotificationWindowManagerServiceDelegate) {
    this.listeners.delete(listener);
  }

  // ---------------------------------------------------------------------------
  // INotificationWindowService
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // When the notification become visible, we reset the timer so that the time
  // is counted when the notification is in view
  async notificationBecameVisible() {
    this.logger.info('Recommendation became visible, restarting timer');
    this.stopIdleTimer();
    this.timerService.start();
    this.notificationVisibilityStopWatch.start();

    await this.handleNotificationViewed();
  }

  // ---------------------------------------------------------------------------
  getNotificationStopWatchTime() {
    return this.notificationVisibilityStopWatch.getTime();
  }

  // ---------------------------------------------------------------------------
  async getActiveNotification(): Promise<INotificationDisplayDetails> {
    return this.activeNotificationInfo;
  }

  // ---------------------------------------------------------------------------
  closeNotificationWindow() {
    this.close();
  }

  // ---------------------------------------------------------------------------
  toggleTimeoutTimer(pause: boolean) {
    if (pause) {
      this.stopIdleTimer();
      return;
    }

    this.timerService.start();
  }

  // ---------------------------------------------------------------------------
  // OWWindowsListenerDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  onAltF4Blocked(windowName: string) {
    this.logger.info(
      `received Alt+F4 key combination on windowId: ${windowName} `,
    );

    if (!windowName.endsWith(kNotificationWindowName)) {
      return;
    }

    const visibilityTimeInSeconds =
      this.notificationVisibilityStopWatch.getTime() / 1000;
    this.notificationVisibilityStopWatch.stop();

    this.userActionService.notificationDismissed(
      this.activeNotificationInfo,
      visibilityTimeInSeconds,
    );

    this.close();
  }

  // ---------------------------------------------------------------------------
  onWindowClosed(windowName: string) {
    if (!windowName.endsWith(kNotificationWindowName)) {
      return;
    }

    this.stopIdleTimer();
    this.unregisterListeners();
    this.logger.info(`Notification window, windowId: "${windowName}" closed,`);
    const visibilityTimeInSeconds =
      this.notificationVisibilityStopWatch.getTime() / 1000;
    this.notificationVisibilityStopWatch.stop();

    if (!this.isTesterNotification) {
      this.triggerListenerEvent(
        'onNotificationWindowClosed',
        this.activeNotificationInfo,
        visibilityTimeInSeconds,
      );
    }
  }

  // ---------------------------------------------------------------------------
  // TimerServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  onTimer() {
    this.logger.info(
      'Notification timer has reached an end, closing notification ',
    );

    const visibilityTimeInSeconds =
      this.notificationVisibilityStopWatch.getTime() / 1000;
    this.notificationVisibilityStopWatch.stop();

    this.analyticsService.reportContentRecommend(
      this.activeNotificationInfo.targetGameId ||
        this.activeNotificationInfo.gameId,

      this.activeNotificationInfo.applicationUid,
      ContentRecommendationUserAction.Timeout,
      visibilityTimeInSeconds,
      this.activeNotificationInfo.originalGameId,
    );
    this.close();
  }
}
